import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import useStore from "../../Store/Store";
import PaymentComponent from "../Common/PaymentComponent/PaymentComponent";
import { PaymentResult, PaymentError } from "../Common/PaymentComponent/types";

interface ModelSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedModel: string) => void;
}

const ModelSelectionDialog: React.FC<ModelSelectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { selectedModel, setSelectedModel } = useStore();
  const [localSelectedModel, setLocalSelectedModel] = useState(selectedModel);
  const [showPayment, setShowPayment] = useState(false);

  const modelOptions = [
    { value: "gemini-2.5-flash-preview-05-20", label: "Gemini 2.5 Flash" },
    {
      value: "gemini-2.5-pro",
      label: "Gemini 2.5 Pro (June 2025 Edition) - $2.29/generation",
    },
  ];

  const handleModelChange = (model: string) => {
    setLocalSelectedModel(model);
  };

  const handleContinue = () => {
    const modelToUse = localSelectedModel;

    // Check if Pro model is selected
    if (modelToUse === "gemini-2.5-pro") {
      setShowPayment(true);
      return;
    }

    // For other models, proceed directly
    setSelectedModel(modelToUse);
    onConfirm(modelToUse);
    onClose();
  };

  const handlePaymentSuccess = (paymentResult: PaymentResult) => {
    console.log("Payment successful:", paymentResult);
    const modelToUse = localSelectedModel;
    setSelectedModel(modelToUse);
    onConfirm(modelToUse);
    setShowPayment(false);
    onClose();
  };

  const handlePaymentError = (error: PaymentError) => {
    console.error("Payment failed:", error);
    // Stay on payment screen, user can try again
    alert(`Payment failed: ${error.message}`);
  };

  const handlePaymentCancel = () => {
    // Go back to model selection
    setShowPayment(false);
  };

  if (showPayment) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "30px",
            backgroundColor: "#fff",
            padding: "24px",
          },
        }}
      >
        <DialogContent>
          <PaymentComponent
            amount={229} // $2.29 in cents
            description="Gemini 2.5 Pro Access - $2.00 model cost + $0.29 our markup"
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onPaymentCancel={handlePaymentCancel}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handlePaymentCancel}
            sx={{
              borderRadius: "30px",
              textTransform: "none",
              width: "300px",
              fontWeight: "bold",
              color: "#fff",
              border: "2px solid #FFAEA6",
              backgroundColor: "#FFAEA6",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#FFAEA6",
                fontWeight: "normal",
                border: "2px solid #FFAEA6",
              },
            }}
          >
            Back to Model Selection
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "30px",
          backgroundColor: "#fff",
          padding: "24px",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "24px",
          marginBottom: "8px",
        }}
      >
        Choose Your AI Model
      </DialogTitle>
      <DialogContent>
        {/* Gemini Comparison Section */}
        <Box sx={{ marginBottom: "24px", textAlign: "center" }}>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              fontSize: "16px",
              marginBottom: "16px",
              lineHeight: 1.5,
            }}
          >
            <strong>Why upgrade to Gemini 2.5 Pro?</strong>
          </Typography>
          <Box sx={{ textAlign: "left", marginLeft: "20px" }}>
            <Typography
              variant="body2"
              sx={{ color: "#666", fontSize: "14px", marginBottom: "8px" }}
            >
              • <strong>55% better advanced reasoning</strong> - Superior
              performance on complex logic and analysis tasks
            </Typography>
          </Box>
        </Box>

        <FormControl fullWidth>
          <InputLabel
            sx={{
              color: "#666",
              "&.Mui-focused": { color: "#666" },
            }}
          >
            Select Model
          </InputLabel>
          <Select
            value={localSelectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            label="Select Model"
            sx={{
              borderRadius: "30px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ddd",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFAEA6",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFAEA6",
              },
            }}
          >
            {modelOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  borderRadius: "30px",
                  margin: "4px 8px",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "#FFAEA6",
                    color: "#fff",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#FFAEA6",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#FFAEA6",
                    },
                  },
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          padding: "16px 24px",
        }}
      >
        <Button
          onClick={handleContinue}
          disabled={!localSelectedModel}
          sx={{
            backgroundColor: "#FFAEA6",
            color: "#fff",
            fontWeight: "bold",
            borderRadius: "30px",
            padding: "12px 32px",
            textTransform: "none",
            fontSize: "16px",
            border: "2px solid transparent",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#FFAEA6",
              fontWeight: "normal",
              border: "2px solid #FFAEA6",
            },
            "&:disabled": {
              backgroundColor: "#f5f5f5",
              color: "#ccc",
            },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelSelectionDialog;
